import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface TabsController {
  selectedIndex: number;
  goToTab: (index: number) => void;
}

const TabsContext = createContext<TabsController>(undefined!);

type TabsProviderProps = PropsWithChildren<{
  urlParameterName?: string;
}>;

export const TabsProvider: FC<TabsProviderProps> = ({ children, urlParameterName }: TabsProviderProps) => {
  const tab = urlParameterName ?? "tab";

  const navigate = useNavigate();
  const location = useLocation();

  // Set the initial selectedIndex from the URL, or default to 0 if it's invalid
  const [selectedIndex, setSelectedIndex] = useState(() => {
    const val = Number(new URLSearchParams(location.search).get(tab));
    return isNaN(val) ? 0 : val;
  });

  const goToTab = (index: number) => {
    const params = new URLSearchParams(location.search);
    params.set(tab, `${index}`);
    navigate({ search: params.toString() });
  };
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = Number(params.get(tab));
    if (!isNaN(t) && t !== selectedIndex) {
      setSelectedIndex(t);
    }
  }, [location.search, tab]);

  return <TabsContext.Provider value={{ selectedIndex, goToTab }}>{children}</TabsContext.Provider>;
};

export const useTabs = (): TabsController => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("useTabs must be used within a TabsProvider");
  }
  return context;
};
